"use client";

import { useState, useRef } from "react";
import { content } from "@/config/content";
import { usePageSize, PageSizeSelect } from "@/lib/usePageSize";
import { sortCategories } from "@/lib/sortCategories";
import { useSiteSettings } from "@/lib/useSiteSettings";
import FilterTabs from "./FilterTabs";
import ArtworkCard from "./ArtworkCard";

export default function Gallery({ artworks, onQuickView }) {
  const [activeFilter, setActiveFilter] = useState(content.gallery.filterAll);
  const [currentPage, setCurrentPage] = useState(1);
  const { pageSize, setPageSize, PAGE_OPTIONS } = usePageSize();
  const galleryRef = useRef(null);
  // Phase 3.8 — gallery pagination copy from CMS
  const { settings: g } = useSiteSettings("gallery");

  // 過濾掉隱藏和已刪除的作品，使用 imageV2 版本圖片
  const displayArtworks = artworks
    .filter((a) => a.visible !== false && !a.deletedAt)
    .map((a) => ({ ...a, image: a.imageV2 || a.image }));

  // 從實際資料動態產生分類（取代 hardcoded config）
  const dynamicFilters = sortCategories([
    ...new Set(displayArtworks.map((a) => a.category).filter(Boolean)),
  ]);

  const filtered =
    activeFilter === content.gallery.filterAll
      ? displayArtworks
      : displayArtworks.filter((a) => a.category === activeFilter);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const visible = filtered.slice(startIdx, startIdx + pageSize);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // 產生頁碼列表:最多顯示 7 個頁碼 + 省略號
  const getPageNumbers = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages,
      );
    }
    return pages;
  };

  return (
    <section
      ref={galleryRef}
      className=""
      style={{
        padding: "var(--spacing-sectionGap) var(--spacing-pagePadding)",
      }}
    >
      <h2
        className="text-2xl md:text-3xl font-bold text-center mb-8"
        style={{
          fontFamily: "var(--font-heading)",
          color: "var(--color-text)",
        }}
      >
        {g.gallery_title || content.gallery.title}
      </h2>

      <div className="max-w-5xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <FilterTabs
          active={activeFilter}
          onChange={handleFilterChange}
          filters={dynamicFilters}
        />
        <PageSizeSelect
          pageSize={pageSize}
          onChange={(n) => {
            setPageSize(n);
            setCurrentPage(1);
          }}
          options={PAGE_OPTIONS}
        />
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
        {visible.map((artwork, idx) => (
          <div key={artwork.id}>
            <ArtworkCard
              artwork={artwork}
              onQuickView={onQuickView}
              priority={currentPage === 1 && idx < 3}
            />
          </div>
        ))}
      </div>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-3 mt-10">
          <div className="flex items-center gap-1.5">
            {/* 上一頁 */}
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-11 px-3 flex items-center justify-center text-sm font-medium gap-1 transition-opacity"
              style={{
                borderRadius: "var(--radius-full)",
                border: "none",
                backgroundColor:
                  currentPage === 1
                    ? "var(--color-cardBorder)"
                    : "var(--color-primary)",
                color: "#fff",
                opacity: currentPage === 1 ? 0.4 : 1,
                cursor: currentPage === 1 ? "default" : "pointer",
              }}
            >
              {g.gallery_prev_page || "← 上一頁"}
            </button>

            {/* 頁碼 */}
            {getPageNumbers().map((page, i) =>
              page === "..." ? (
                <span
                  key={`dots-${i}`}
                  className="w-11 h-11 flex items-center justify-center text-sm"
                  style={{ color: "var(--color-textMuted)" }}
                >
                  ⋯
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className="w-11 h-11 flex items-center justify-center text-sm font-medium transition-all"
                  style={{
                    borderRadius: "var(--radius-full)",
                    border:
                      page === currentPage
                        ? "none"
                        : "1px solid var(--color-cardBorder)",
                    backgroundColor:
                      page === currentPage
                        ? "var(--color-primary)"
                        : "var(--color-cardBg)",
                    color: page === currentPage ? "#fff" : "var(--color-text)",
                    cursor: "pointer",
                  }}
                >
                  {page}
                </button>
              ),
            )}

            {/* 下一頁 */}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-11 px-3 flex items-center justify-center text-sm font-medium gap-1 transition-opacity"
              style={{
                borderRadius: "var(--radius-full)",
                border: "none",
                backgroundColor:
                  currentPage === totalPages
                    ? "var(--color-cardBorder)"
                    : "var(--color-primary)",
                color: "#fff",
                opacity: currentPage === totalPages ? 0.4 : 1,
                cursor: currentPage === totalPages ? "default" : "pointer",
              }}
            >
              {g.gallery_next_page || "下一頁 →"}
            </button>
          </div>

          <p className="text-xs" style={{ color: "var(--color-textMuted)" }}>
            {(
              g.gallery_page_info ||
              "第 {current} 頁 / 共 {total} 頁（{count} 張作品）"
            )
              .replace("{current}", currentPage)
              .replace("{total}", totalPages)
              .replace("{count}", filtered.length)}
          </p>
        </div>
      )}

      {filtered.length === 0 && (
        <p
          className="text-center py-12"
          style={{
            color: "var(--color-textMuted)",
            fontFamily: "var(--font-body)",
          }}
        >
          {g.gallery_empty_category || "這個分類還沒有作品"}
        </p>
      )}
    </section>
  );
}
